import React from "react";
import { Star, ArrowLeft, BookOpen } from "lucide-react";
import PageHero, { EYEBROW } from "@/components/PageHero";
import SEO from "@/components/SEO";
import ShareButtons from "@/components/ShareButtons";
import Reveal from "@/components/motion/Reveal";
import Section from "@/components/ui/Section";
import CTAButton, { ArrowLink } from "@/components/ui/CTAButton";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

const FALLBACK_BG = "https://images.unsplash.com/photo-1507692049790-de58290a4334?w=1920&q=85";

// El backend ya garantiza ISO YYYY-MM-DD; si llegara otra cosa se muestra tal cual.
const fmtDate = d => {
  if (!d) return "";
  const parsed = new Date(`${d}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export default function PalabraDelDia() {
  // El `.catch` original se tragaba el error y pintaba «No hay reflexión
  // disponible para hoy»: durante días el endpoint respondió 500 y la página lo
  // presentaba como si sencillamente no se hubiera publicado nada. Un fallo de
  // carga y un día sin evangelio no son lo mismo y no pueden verse igual.
  const { data: word, loading, error } = useCachedResource("word-of-day", () => apiGet("/word-of-day"));

  const resumen = (word?.reflection || word?.text || "").slice(0, 160);

  return (
    <div data-testid="palabra-del-dia-page">
      <SEO
        title="Palabra del Día · Evangelio y reflexión"
        description={resumen || "El Evangelio de cada día con una breve reflexión, de la Delegación de Evangelización Digital de la Diócesis de Huelva."}
        image={word?.image_url || word?.image || undefined}
        type="article"
      />

      {/* Esta página se dibujaba su propio héroe a mano, uno de los cuatro
          duplicados que existían. Ahora reutiliza PageHero pasándole el
          contenido directamente. */}
      <PageHero
        content={{
          subtitle: EYEBROW,
          title: "La Palabra",
          title_italic: "en el Día",
          bg_image: FALLBACK_BG,
          overlay_opacity: 0.65,
        }}
      >
        {word?.date && (
          <p className="text-sm text-white/60 first-letter:uppercase">{fmtDate(word.date)}</p>
        )}
      </PageHero>

      <Section tone="light" width="narrow">
        <ArrowLink to="/" data-testid="back-to-home-link" className="text-muted-foreground hover:text-primary mb-10">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver a inicio
        </ArrowLink>

        {loading && !word ? (
          <div className="space-y-8">
            <Skeleton className="aspect-video rounded-2xl" />
            <Skeleton className="h-48 rounded-2xl" />
            <Skeleton className="h-32 rounded-2xl" />
          </div>
        ) : word ? (
          <div className="space-y-8">
            {/* Imagen destacada. El contenedor no reservaba altura —solo
                `max-height`—, así que la cita y la reflexión bajaban de golpe
                cuando la imagen terminaba de decodificar. */}
            {(word.image_url || word.image) && (
              <Reveal distance={24}>
                <SmartImage
                  src={word.image_url || word.image}
                  alt="Ilustración del Evangelio del día"
                  ratio="16 / 9"
                  rounded="rounded-2xl"
                  priority
                  className="shadow-rest"
                  data-testid="palabra-dia-image"
                />
              </Reveal>
            )}

            {/* Cita del Evangelio */}
            <Reveal delay={80} distance={24}>
              <div
                className="rounded-2xl p-8 md:p-10 text-white"
                style={{ background: "linear-gradient(135deg, #1868C2 0%, #0E4E99 100%)" }}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60 mb-5 flex items-center gap-2">
                  <Star className="w-3.5 h-3.5" aria-hidden="true" /> Cita del Evangelio
                </p>
                <blockquote className="font-spiritual italic text-2xl md:text-3xl leading-relaxed mb-4">
                  {word.text}
                </blockquote>
                <cite className="text-sm font-semibold text-white/80 not-italic">{word.reference}</cite>
              </div>
            </Reveal>

            {/* Reflexión */}
            {word.reflection && (
              <Reveal delay={140} distance={24}>
                <div className="bg-muted rounded-2xl p-8 border border-border" data-testid="palabra-dia-reflection">
                  <div className="flex items-center gap-2 mb-5">
                    <BookOpen className="w-4 h-4 text-primary" aria-hidden="true" />
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Reflexión</p>
                  </div>
                  {/* whitespace-pre-line: la reflexión trae saltos de línea reales
                      (\n) entre párrafos, y sin esta clase el navegador los colapsa
                      y lo muestra todo pegado en un bloque único. El archivo
                      (EvangeliosAnteriores.js) ya lo tenía bien; aquí faltaba. */}
                  <p className="text-base leading-relaxed whitespace-pre-line">{word.reflection}</p>
                </div>
              </Reveal>
            )}

            <Reveal delay={180} distance={16}>
              <div className="flex justify-center pt-2">
                <ShareButtons
                  url="/palabra-del-dia"
                  title={`${word.reference} — Palabra del Día`}
                  description={resumen}
                  hashtags="Evangelio,EvangelizaciónDigital,Huelva"
                />
              </div>
            </Reveal>

            <div className="pt-2 flex justify-center">
              <CTAButton to="/palabra-del-dia/archivo" data-testid="gospel-archive-link" className="font-semibold">
                <BookOpen className="w-4 h-4" aria-hidden="true" /> Ver evangelios anteriores
              </CTAButton>
            </div>
          </div>
        ) : error ? (
          <EmptyState
            icon={Star}
            title="No se ha podido cargar la Palabra del Día"
            description="Vuelve a intentarlo en unos minutos."
            data-testid="palabra-dia-error"
          />
        ) : (
          <EmptyState
            icon={Star}
            title="No hay reflexión disponible para hoy"
            description="Puedes consultar los evangelios de días anteriores mientras tanto."
            action={
              <CTAButton to="/palabra-del-dia/archivo" variant="outline">
                Ver evangelios anteriores
              </CTAButton>
            }
          />
        )}
      </Section>
    </div>
  );
}
